/**
 * LabelLines — a linha vertical que liga a etiqueta elevada ao avatar, para TODOS
 * os jogadores em UMA única chamada de desenho.
 *
 * Antes cada jogador tinha o seu próprio `Mesh` de linha. Com 500 jogadores isso
 * eram até 500 draw calls por quadro só de linhas — e draw call é justamente o que
 * derruba o fps numa GPU integrada (o custo é de CPU/driver, não de pixel). Aqui
 * tudo vira um InstancedMesh: 1 chamada, independentemente da população.
 *
 * Por que juntar é seguro com material TRANSPARENTE: normalmente mesclar
 * transparências muda o resultado, porque o blend depende da ordem de desenho. Só
 * que TODAS as linhas têm exatamente a mesma cor e a mesma opacidade — e o blend
 * "source-over" de duas camadas com cor e alfa idênticos dá o mesmo pixel em
 * qualquer ordem. A ordem em relação ao RESTO da cena também é preservada: o
 * `renderOrder` 998 continua colocando as linhas antes dos sprites (999).
 *
 * A linha ESCONDIDA usa matriz de escala 0 em vez de sair da lista: manter as
 * linhas em posições fixas evita o embaralhamento de índices a cada quadro (a
 * visibilidade oscila com o de-clutter), e uma instância degenerada não rasteriza
 * nenhum pixel.
 */
import * as THREE from 'three';
import { probes, P } from '../perfProbes.js';

const CAP = 2048; // mesma capacidade do Fleet/BlobShadows
const IN_N = 6;   // entradas que definem a matriz da linha: x, y, z, s, bottom, top
// estados do cache por slot (hasIn): 0 = desconhecido (força escrever),
// 1 = visível com as entradas de lastIn, 2 = escondida (matriz zero)
const ST_NONE = 0, ST_VIS = 1, ST_HID = 2;
const _m = new THREE.Matrix4();
const _pos = new THREE.Vector3();
const _scl = new THREE.Vector3();
const _q = new THREE.Quaternion(); // identidade (a linha é sempre vertical)
const _ZERO = new THREE.Matrix4().makeScale(0, 0, 0);

export class LabelLines {
  /** @param {THREE.Object3D} parent grupo das etiquetas (herda o liga/desliga) */
  constructor(parent) {
    const geo = new THREE.CylinderGeometry(0.035, 0.035, 1, 6);
    // depthTest:false → a linha também fica sempre por cima dos prédios
    const mat = new THREE.MeshBasicMaterial({
      color: '#3dff86', transparent: true, opacity: 0.5, depthWrite: false, depthTest: false,
      fog: false, toneMapped: false // chapada, como o rotulo (ver LabelAtlas)
    });
    this.mesh = new THREE.InstancedMesh(geo, mat, CAP);
    this.mesh.name = 'labelLines';
    this.mesh.count = 0;
    this.mesh.renderOrder = 998; // antes dos sprites das etiquetas (999)
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.frustumCulled = false; // instâncias espalhadas pela cidade
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    parent.add(this.mesh);
    this.count = 0;
    this.slotRef = new Array(CAP).fill(null);
    // Pulo de escrita + faixa de upload, o MESMO contrato do BlobShadows: a matriz é
    // função pura das 6 entradas (ou do estado "escondida"), então entradas repetidas
    // são puladas e o flush sobe só a faixa que mudou. Antes o flush marcava
    // needsUpdate sem faixa — o three reenvia o array INTEIRO (2.048 × 64 B = 128 KB)
    // por quadro, o mesmo defeito que as sombras tinham.
    this.lastIn = new Float64Array(CAP * IN_N);
    this.state = new Uint8Array(CAP); // ST_NONE | ST_VIS | ST_HID
    this._lo = -1;
    this._hi = -1;
  }

  /** Amplia a faixa de linhas sujas a enviar neste quadro. */
  _touch(slot) {
    if (this._lo < 0 || slot < this._lo) this._lo = slot;
    if (slot > this._hi) this._hi = slot;
  }

  /** Reserva um slot de instância para uma entidade. -1 se lotado. */
  acquire(ref) {
    if (this.count >= CAP) return -1;
    const slot = this.count++;
    this.slotRef[slot] = ref;
    this.mesh.setMatrixAt(slot, _ZERO); // nasce escondida (o layout decide no 1º quadro)
    this.state[slot] = ST_HID;
    this._touch(slot);
    this.mesh.count = this.count;
    return slot;
  }

  /** Libera o slot (swap-remove): a última linha assume o buraco. */
  release(slot) {
    if (slot < 0) return;
    const last = this.count - 1;
    if (slot !== last && last >= 0) {
      const moved = this.slotRef[last];
      if (moved) moved.lineSlot = slot;
      this.slotRef[slot] = moved;
      const a = this.mesh.instanceMatrix.array;
      a.copyWithin(slot * 16, last * 16, last * 16 + 16);
      this._touch(slot); // a troca mexeu no buffer sem passar pelo write
    }
    // slot trocou de dono (ou esvaziou) → o cache não vale mais; força reescrever
    this.state[slot] = ST_NONE;
    this.state[last] = ST_NONE;
    this.slotRef[last] = null;
    this.count = Math.max(0, last);
    this.mesh.count = this.count;
  }

  /**
   * Posiciona a linha entre `bottom` e `top` (alturas LOCAIS do grupo da etiqueta,
   * antes da escala) acima do ponto (x, y, z) do avatar.
   * @param {number} s escala do grupo da etiqueta (carro ou pessoa)
   */
  write(slot, x, y, z, s, bottom, top) {
    if (slot < 0) return;
    probes.begin(P.linhas);
    const L = this.lastIn, o = slot * IN_N;
    // mesmas 6 entradas e já visível → a matriz sairia bit a bit igual (comparação
    // EXATA de propósito: o pulo é demonstravelmente invisível, não "quase igual")
    if (this.state[slot] === ST_VIS &&
        L[o] === x && L[o + 1] === y && L[o + 2] === z &&
        L[o + 3] === s && L[o + 4] === bottom && L[o + 5] === top) { probes.end(P.linhas); return; }
    L[o] = x; L[o + 1] = y; L[o + 2] = z; L[o + 3] = s; L[o + 4] = bottom; L[o + 5] = top;
    this.state[slot] = ST_VIS;
    const h = top - bottom;
    _pos.set(x, y + (bottom + h / 2) * s, z);
    _scl.set(s, h * s, s);
    _m.compose(_pos, _q, _scl);
    this.mesh.setMatrixAt(slot, _m);
    this._touch(slot);
    probes.end(P.linhas);
  }

  /** Esconde a linha do slot (instância degenerada — não rasteriza). */
  hide(slot) {
    if (slot < 0 || this.state[slot] === ST_HID) return; // já escondida → nada a fazer
    probes.begin(P.linhas);
    this.state[slot] = ST_HID;
    this.mesh.setMatrixAt(slot, _ZERO);
    this._touch(slot);
    probes.end(P.linhas);
  }

  /** Sobe à GPU só a faixa que mudou (nada mudou → não toca no buffer). */
  flush() {
    if (this._lo < 0) return;
    probes.begin(P.linhas);
    const a = this.mesh.instanceMatrix;
    a.clearUpdateRanges();
    a.addUpdateRange(this._lo * 16, (this._hi - this._lo + 1) * 16);
    a.needsUpdate = true;
    this._lo = this._hi = -1;
    probes.end(P.linhas);
  }
}
